import { CONFIG } from "../config.ts";
import { clamp, damp } from "../math.ts";

// Follows the player and eases zoom out as the player grows. Also owns a small
// screen-shake impulse used for juice.
export class Camera {
  x: number;
  y: number;
  zoom: number;
  private shake = 0;

  constructor(x: number, y: number) {
    this.x = x;
    this.y = y;
    this.zoom = CONFIG.camera.baseZoom;
  }

  private targetZoom(stage: number): number {
    const z = CONFIG.camera.baseZoom + CONFIG.camera.zoomPerStage * stage;
    return Math.max(CONFIG.camera.minZoom, z);
  }

  kick(amount: number) {
    this.shake = Math.min(this.shake + amount, 24);
  }

  update(dt: number, targetX: number, targetY: number, stage: number) {
    this.x = damp(this.x, targetX, CONFIG.camera.follow, dt);
    this.y = damp(this.y, targetY, CONFIG.camera.follow, dt);
    this.zoom = damp(this.zoom, this.targetZoom(stage), 3, dt);
    this.shake = damp(this.shake, 0, 8, dt);
  }

  /** Apply the world transform to the context. Call between save()/restore(). */
  apply(ctx: CanvasRenderingContext2D, viewW: number, viewH: number) {
    const sx = (Math.random() - 0.5) * this.shake;
    const sy = (Math.random() - 0.5) * this.shake;
    ctx.translate(viewW / 2 + sx, viewH / 2 + sy);
    ctx.scale(this.zoom, this.zoom);
    ctx.translate(-this.x, -this.y);
  }

  /** Visible world half-extents, for culling. */
  halfExtents(viewW: number, viewH: number) {
    return { hw: viewW / 2 / this.zoom, hh: viewH / 2 / this.zoom };
  }

  clampToWorld(viewW: number, viewH: number) {
    const { hw, hh } = this.halfExtents(viewW, viewH);
    // Only clamp when the world is bigger than the view on that axis.
    if (CONFIG.world.width > hw * 2) this.x = clamp(this.x, hw, CONFIG.world.width - hw);
    if (CONFIG.world.height > hh * 2) this.y = clamp(this.y, hh, CONFIG.world.height - hh);
  }
}
